import { staticImageUrl } from '../util/images';
// @ts-expect-error JSON import handled by bundler
import portfolioCopy from '../static/copy/portfolios.json';
import { Countries } from '@wren/shared';
import { SerializedUser } from '../types';

const Afforestation = staticImageUrl('wren-icon-black-conservation.png');
const Capture = staticImageUrl('offset-strategy-2.png');
const Tech = staticImageUrl('offset-strategy-3.png');
const amex = staticImageUrl('card-amex.png');
const dinersclub = staticImageUrl('card-dinersclub.png');
const discover = staticImageUrl('card-discover.png');
const jcb = staticImageUrl('card-jcb.png');
const mastercard = staticImageUrl('card-mastercard.png');
const unionpay = staticImageUrl('card-unionpay.png');
const visa = staticImageUrl('card-visa.png');
const unknown = staticImageUrl('card-unknown.png');

export const typeIcon = (type: string): string | null => {
  if (type === 'Conservation') return Afforestation;
  if (type === 'Afforestation') return Afforestation;
  if (type === 'Clean Cookstoves') return Tech;
  if (type === 'Small Biogas Digestors') return Capture;
  return null;
};

// Previously an R.curry'd no-op tap. It has no callers and never partially
// applied, so a plain binary function is equivalent.
export const tapLog = (message: string, x: unknown): unknown => {
  return x;
};

const splitText = (x: string) => x.split('\n');
// Was `R.reject(R.isEmpty, x)`. R.isEmpty is true for '' on strings, so
// rejecting empties is the same as keeping every non-empty entry.
const removeEmptyStrings = (x: string[]) => x.filter((s) => s !== '');
// Was `R.compose(removeEmptyStrings, splitText)(x)`; compose runs right-to-left,
// i.e. splitText first, so this is a direct nested call.
export const createBodyText = (x: string): string[] =>
  removeEmptyStrings(splitText(x));

// In the four metric converters below, `x != null` replaces `!R.isNil(x)`.
// R.isNil is true only for null/undefined (not for NaN or 0), which is exactly
// what the loose `!= null` check matches.
export const toMetricSqFt = (sqft: number): number => {
  if (sqft != null) return Number(sqft / 10.764);
  throw Error('Cannot convert metric on nil');
};
export const fromMetricSqFt = (sqmeters: number): number => {
  if (sqmeters != null) return Number(sqmeters * 10.764);
  throw Error('Cannot convert metric on nil');
};
export const toMetricMiles = (miles: number): number => {
  if (miles != null) return Number(miles * 1.609344);
  throw Error('Cannot convert metric on nil');
};
export const fromMetricMiles = (km: number): number => {
  if (km != null) return Number(km / 1.609344);
  throw Error('Cannot convert metric on nil');
};

// 1 = 112903/480 (liters per 100km) * (miles per gallon)
// or equivalently, there are 112903 liter miles per 48000 km gallons
// https://www.wolframalpha.com/input?i=112903%2F480+%28liters+per+100km%29+*+%28miles+per+gallon%29
const litermilesper100kmgallon = 112903 / 480;

const toMetricMpg = (mpg: number): number => litermilesper100kmgallon / mpg;
const fromMetricMpg = (literPer100Km: number): number =>
  litermilesper100kmgallon / literPer100Km;

export const handleUnitConversion = (
  value: number,
  startingUnit: string,
  endingUnit: string
): number => {
  if (startingUnit === endingUnit) return value;

  if (startingUnit === 'square feet' && endingUnit === 'square meters')
    return toMetricSqFt(value);
  if (startingUnit === 'square meters' && endingUnit === 'square feet')
    return fromMetricSqFt(value);
  if (startingUnit === 'miles' && endingUnit === 'km')
    return toMetricMiles(value);
  if (startingUnit === 'km' && endingUnit === 'miles')
    return fromMetricMiles(value);
  if (startingUnit === 'mpg' && endingUnit === 'l/100km')
    return toMetricMpg(value);
  if (startingUnit === 'l/100km' && endingUnit === 'mpg')
    return fromMetricMpg(value);
  throw Error(`Impossible Unit Conversion: ${startingUnit} to ${endingUnit}`);
};

export const truncate = (
  input: string | undefined,
  maxLength: number
): string | undefined => {
  if (input && input.length > maxLength) {
    return `${input.substring(0, maxLength)}...`;
  }
  return input;
};

export const getEmojiForCountryName = (country: string): string => {
  const matchingCountry = Countries.COUNTRIES[country];
  if (matchingCountry) {
    return matchingCountry.flag_emoji;
  }
  return '';
};

const FLAG_CDN_BASE = 'https://flagcdn.com';

/**
 * Returns a flag image URL for reliable display when emoji glyphs don't
 * render (e.g. on some Windows systems). Accepts country name or country
 * object with two_char_country_code.
 */
export const getFlagImageUrl = (
  country: string | { two_char_country_code?: string }
): string | null => {
  const match =
    typeof country === 'string' ? Countries.COUNTRIES[country] : country;
  const code = match?.two_char_country_code;
  if (!code) return null;
  return `${FLAG_CDN_BASE}/w40/${String(code).toLowerCase()}.png`;
};

export const percentOffsetByAmount = (amount: number, total: number): number =>
  (amount / total) * 100;

export const tonsOffsetByAmount = (
  amount: number,
  annualFootprint: number,
  total: number
): string =>
  (
    ((annualFootprint / 12) * percentOffsetByAmount(amount, total)) /
    100
  ).toFixed(1);

export const tonsInCarsByAmount = (
  amount: number,
  annualFootprint: number,
  total: number
): string =>
  (Number(tonsOffsetByAmount(amount, annualFootprint, total)) / 0.38).toFixed(
    2
  );

export const fixedDecimal = (num: number | string, dec?: number): string => {
  // `dec ?? 0` replaces `R.defaultTo(0, dec)`; both substitute 0 only for
  // null/undefined (R.defaultTo also falls back on NaN, but callers never pass
  // NaN here).
  const decimal = dec ?? 0;
  return Number(num).toFixed(decimal);
};

// Was built with R.type/R.append/R.last/R.map/R.init. `Array.isArray` replaces
// `R.type(x) === 'Array'`, and appending the last element to the comma-suffixed
// earlier elements is the same as mapping all-but-last then pushing the last.
export const renderArray = (
  answers: unknown[] | number | string
): string | string[] => {
  if (Array.isArray(answers)) {
    return [
      ...answers.slice(0, -1).map((a) => `${a}, `),
      `${answers[answers.length - 1]}`,
    ];
  }
  // `R.type(x) === 'Number'` becomes `typeof x === 'number'`.
  if (typeof answers === 'number') return `${answers.toFixed(0)} `;
  return `${answers}`;
};

export const percentDiff = (numerator: number, denom: number): string =>
  fixedDecimal((numerator / denom - 1) * 100);

export const diffInWords = (num: number): string => {
  switch (Math.sign(num)) {
    case 1:
      return `${num}% more than`;
    case -1:
      return `${Math.abs(num)}% less than`;
    default:
      return 'the same as ';
  }
};

// `dec ?? 0` replaces `R.defaultTo(0, dec)` (see fixedDecimal above).
export const handleToFixed = (
  stringToFix: number | null | undefined,
  dec?: number
): string | 0 => (stringToFix ? stringToFix.toFixed(dec ?? 0) : 0);

export const guessCountry = (languageCode: string): string | null => {
  // Was an R.filter(R.has('language_code')) -> R.filter(R.includes) -> R.head ->
  // R.prop('country_name') pipeline; a single `.find` that checks the same
  // condition and reads `country_name` is equivalent (find returns the first
  // match, mirroring R.head of the filtered list).
  //
  // `language_code` is not part of the Country type or the underlying data, so
  // in practice no country matches and this returns null (callers fall back to
  // a default). Kept for parity with the previous behavior.
  const match = Countries.alphabetical().find((country) => {
    const languageCodes = (country as { language_code?: string[] })
      .language_code;
    return Array.isArray(languageCodes) && languageCodes.includes(languageCode);
  });
  return match ? match.country_name : null;
};

// `prop != null` replaces the two `!R.isNil(prop)` guards (see the metric
// converters above for why the two are equivalent here).
export const handleConversion = (
  isMetric: boolean,
  prop: number | null | undefined,
  startUnit: string,
  endUnit: string
): string | number | null | undefined =>
  isMetric && prop != null
    ? handleUnitConversion(prop, startUnit, endUnit).toFixed(0)
    : prop != null
      ? Number(prop)
      : prop;

export const cardBrandImage = (type: string): string => {
  switch (type.toLowerCase()) {
    case 'american express':
      return amex;
    case 'diners club':
      return dinersclub;
    case 'jcb':
      return jcb;
    case 'discover':
      return discover;
    case 'mastercard':
      return mastercard;
    case 'unionpay':
      return unionpay;
    case 'visa':
      return visa;
    case 'unknown':
      return unknown;
    default:
      return unknown;
  }
};

// `str.includes(sub)` replaces `R.includes(sub, str)` — same substring test.
export const isTestEmail = (emailString: string): boolean => {
  return emailString.includes('@wrentest') || emailString.includes('@orgtest');
};

export const parseUTC = (s: string): Date => {
  const b = s.split(/\D/);
  return new Date(
    Date.UTC(
      Number(b[0]),
      Number(b[1]) - 1,
      Number(b[2]),
      Number(b[3]),
      Number(b[4]),
      Number(b[5])
    )
  );
};

// Was `R.curry((key, arr) => R.pipe(R.pluck(key), max)(arr))`. Manually curried
// as `(key) => (arr) => …` to preserve the sole caller's `fn('value')(data)`
// usage; `arr.map((o) => o[key])` replaces R.pluck.
export const findGreatestValueOfKeyInArrayOfObjects =
  (key: string) =>
  (arr: Record<string, number>[]): number =>
    Math.max(...arr.map((obj) => obj[key]));

export function isObjectEmpty(obj: Record<string, unknown> | null | undefined) {
  if (!obj) {
    return true;
  }
  return Object.keys(obj).length === 0;
}

export function findCountryNameByCountryCode(countryCode: string): string {
  let countryName = '';
  const countryFromCode = Countries.alphabetical().find(
    (country) => country.country_code === countryCode
  );
  if (countryFromCode) {
    countryName = countryFromCode.country_name;
  }
  return countryName;
}

// Whether it's safe to auto-overwrite users.currency from country detection
// (e.g. when the user picks a country in the calculator). We avoid this once
// the user has a Stripe customer or is on a team, since their currency at
// that point is meaningful and shouldn't be silently changed by location
// guesses. Manual currency selection (nav, settings, checkout) is not gated
// by this — see docs/features/subscriptions/data-model.md "Currency model".
export function shouldAutoSetCurrencyFromCountry(
  user: Pick<SerializedUser, 'stripeCustomerId' | 'team' | 'ownedTeams'> | null
): boolean {
  return (
    !!user &&
    !user.stripeCustomerId &&
    !user.team &&
    !!user.ownedTeams &&
    !(user.ownedTeams.length > 0)
  );
}

export function areObjectsEquivalent(
  a: Record<string, unknown>,
  b: Record<string, unknown>
): boolean {
  if (Object.values(a).length !== Object.values(b).length) {
    return false;
  }

  for (let i = 0; i < Object.values(a).length; i++) {
    const propName = Object.keys(a)[i];
    if (a[propName] !== b[propName]) {
      return false;
    }
  }
  return true;
}

export function isOdd(num: number): number {
  return num % 2;
}

export function omit<T extends Record<string, unknown>>(
  keys: string[],
  obj: T
): Partial<T> {
  return keys.reduce((a, e) => {
    /* eslint-disable-next-line */
    const { [e]: omitted, ...rest } = a;
    return rest as typeof a;
  }, obj);
}

export function getPortfolioCopy(
  portfolioSlug: string
): (typeof portfolioCopy)[keyof typeof portfolioCopy] {
  const copy = portfolioCopy[portfolioSlug as keyof typeof portfolioCopy];

  if (!copy) {
    return portfolioCopy.default;
  }

  return copy;
}
